<template>
    <div class="stage-progress" v-if="total > 0">
        <div class="stage-progress-road">
            <div class="stage-progress-track"></div>
            <div class="stage-progress-fill" :style="{ width: rider_percent + '%' }"></div>

            <span v-for="(type, i) in types" :key="i"
                :class="['stage-progress-dot', dot_state(i), { 'stage-progress-dot-content': utils.is_content_activity(type), 'stage-progress-dot-small': total > 24 }]"
                :style="{ left: position(i) + '%' }"></span>

            <!-- The theme's rider (cyclist, pilgrim, hat...) - see themes/. -->
            <div class="stage-progress-rider" :style="{ left: rider_percent + '%' }">
                <component :is="theme_def.rider" :color="color" :size="56" :moving="moving" />
            </div>
        </div>
        <div class="stage-progress-count">{{ index + 1 }}<span>/{{ total }}</span></div>
    </div>
</template>

<script>
// Replaces the old "PREGUNTA X / Y" header: one dot per activity of the
// stage, the road filling up to the current one and a cyclist riding along
// it. Purely presentational - who feeds `types`/`index` decides what the
// stage is (utils/stage-progress.js for the live game, StageActivity.vue's
// own list online).
import { get_theme, get_active_theme } from '@/themes'

export default {
    name: 'StageProgress',
    props: {
        // Activity type per stage position (content activities get a
        // different marker than questions).
        types: { type: Array, required: true },
        // Position of the current activity, 0-based.
        index: { type: Number, required: true },
        // The current one counts as done too (Result screen, answered).
        completed: { type: Boolean, default: false },
        color: { type: String, default: '#00a7af' },
        // Theme id (see themes/index.js). Omitted = the live season's.
        theme: { type: String, default: null },
    },
    data(){
        return {
            utils: require('../../../shared/utils'),
            moving: false,
            moving_timer: null,
        };
    },
    computed: {
        total(){
            return this.types.length;
        },
        theme_def(){
            return this.theme ? get_theme(this.theme) : get_active_theme();
        },
        rider_percent(){
            return this.position(this.index);
        }
    },
    watch: {
        // Let the rider animate (spin wheels, walk...) while it glides to
        // the next dot.
        index(){
            this.moving = true;
            clearTimeout(this.moving_timer);
            this.moving_timer = setTimeout(() => { this.moving = false; }, 900);
        }
    },
    beforeUnmount(){
        clearTimeout(this.moving_timer);
    },
    methods: {
        position(i){
            return this.total > 1 ? (i / (this.total - 1)) * 100 : 50;
        },
        dot_state(i){
            if(i < this.index || (i == this.index && this.completed)) return 'stage-progress-dot-done';
            if(i == this.index) return 'stage-progress-dot-current';
            return '';
        }
    }
}
</script>

<style scoped>
    .stage-progress{
        display: flex;
        align-items: flex-end;
        gap: 14px;
        width: 100%;
        min-width: 0;
    }

    /* Side padding keeps the first/last dot (and the rider centred on
       them) inside the bar. */
    .stage-progress-road{
        position: relative;
        flex: 1;
        min-width: 0;
        height: 52px;
        margin: 0 16px;
    }

    .stage-progress-track,
    .stage-progress-fill{
        position: absolute;
        left: 0;
        bottom: 5px;
        height: 4px;
        border-radius: 2px;
    }
    .stage-progress-track{
        right: 0;
        background-color: rgba(255, 255, 255, 0.2);
    }
    .stage-progress-fill{
        background-color: var(--vc-accent-500, #00a7af);
        transition: width 0.9s ease-in-out;
    }

    .stage-progress-dot{
        position: absolute;
        bottom: 7px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        transform: translate(-50%, 50%);
        background-color: var(--vc-ink-900, #17202b);
        border: 2px solid rgba(255, 255, 255, 0.45);
        transition: background-color 0.4s, border-color 0.4s;
    }
    /* Long stages (dozens of activities): dots shrink to plain ticks so
       they don't pile on top of each other. */
    .stage-progress-dot-small{
        width: 6px;
        height: 6px;
        border: 0;
        background-color: rgba(255, 255, 255, 0.35);
    }
    .stage-progress-dot-small.stage-progress-dot-current{
        width: 10px;
        height: 10px;
        border: 2px solid var(--vc-accent-500, #00a7af);
        background-color: #fff;
    }
    /* Content activities (image/video/PDF/HTML): diamond instead of dot. */
    .stage-progress-dot-content{
        border-radius: 2px;
        transform: translate(-50%, 50%) rotate(45deg);
    }
    .stage-progress-dot-done{
        background-color: var(--vc-accent-500, #00a7af);
        border-color: var(--vc-accent-500, #00a7af);
    }
    .stage-progress-dot-current{
        background-color: #fff;
        border-color: var(--vc-accent-500, #00a7af);
    }

    .stage-progress-rider{
        position: absolute;
        bottom: 12px;
        transform: translateX(-50%);
        transition: left 0.9s ease-in-out;
        line-height: 0;
    }

    .stage-progress-count{
        flex-shrink: 0;
        font-family: "Archivo", sans-serif;
        font-weight: 800;
        font-size: 1rem;
        line-height: 1;
        color: #fff;
        padding-bottom: 1px;
    }
    .stage-progress-count span{
        font-weight: 700;
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.55);
    }

    @media (prefers-reduced-motion: reduce){
        .stage-progress-fill,
        .stage-progress-rider{ transition: none; }
    }
</style>
